import type { LayoutRuntime } from "../../../../shared/layouts/registry";
import { renderOddsBoard } from "../../normalize";
import { formatMatchClockHtml } from "../../../livescore/match-status";
import { normalizeOddsFormat } from "../../../../shared/odds";
import { toGmt7Iso } from "../../../../shared/date-time";
import { livescoreRuntime } from "../../../../shared/runtime/livescore-runtime";
import type { RuntimeEntityState } from "../../../../shared/runtime/livescore-runtime";
type Contract = { endpoint?: string; query?: Record<string, unknown>; pollInterval?: number; activeCompanyId?: string; oddsFormat?: string };
type InitialState = { request?: Contract; data?: unknown };
type BoardState = { contract: Contract; companyId: string; entities: RuntimeEntityState; viewIds: string[]; hiddenIds: Set<string>; finishedIds: Set<string>; initialSnapshotLoaded: boolean; requestId: number; timer: number; clockTimer: number; controller: AbortController | null; lastSyncAt: number; unsubscribe: (() => void) | null };
const states = new WeakMap<HTMLElement, BoardState>();
const STORAGE_KEY = "gwd_odds_board_hidden_matches";
const ODDS_DEBUG_MATCH_ID = "vjxm8ghen2kvr6o";
const readHidden = (): Set<string> => { try { const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); return new Set(Array.isArray(value) ? value.map(String) : []); } catch { return new Set(); } };
const writeHidden = (ids: Set<string>) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids])); } catch {} };
// TEMP DEBUG: remove after the odds payload/schema has been verified.
const logOddsPayload = (state: BoardState, source: "initial" | "bookmaker-click") => {
  const matches = state.viewIds.map((id) => state.entities.matches[id]).filter(Boolean);
  const firstWithOdds = matches.find((match: any) => ["asia", "asiaHt", "eu", "euHt", "bs", "bsHt", "cr", "cs", "csHt"].some((key) => match?.[key] || match?.info?.[key] || match?.odds?.[key] || match?.markets?.[key])) || matches[0];
  const markets = firstWithOdds ? Object.fromEntries(["asia", "asiaHt", "eu", "euHt", "bs", "bsHt", "cr", "cs", "csHt"].map((key) => [key, firstWithOdds?.[key] ?? firstWithOdds?.info?.[key] ?? firstWithOdds?.odds?.[key] ?? firstWithOdds?.markets?.[key]])) : {};
  console.groupCollapsed(`[GWD Odds Debug] ${source} | companyId=${state.companyId} | matches=${matches.length}`);
  console.log("request contract:", state.contract);
  console.log("requested companyIds:", state.contract.query?.companyIds);
  console.log("active companyId:", state.companyId);
  console.log("normalized state:", { viewIds: state.viewIds, entities: state.entities });
  console.log("matches:", matches);
  console.log("first match containing odds:", firstWithOdds);
  console.log("raw markets of first odds match:", markets);
  console.table(Object.entries(markets).flatMap(([marketName, raw]) => (Array.isArray(raw) ? raw : raw ? [raw] : []).map((row: any, index) => ({ market: marketName, index, companyId: row?.companyId ?? row?.company_id ?? row?.cid ?? "(missing)", row }))));
  console.groupEnd();
};
const object = (value: unknown): Record<string, any> => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, any> : {};
const requestPage = async (contract: Contract, page: number, signal: AbortSignal): Promise<unknown> => {
  return livescoreRuntime.fetchJson(String(contract.endpoint || "/api/matches/"), { ...(contract.query || {}), page }, signal);
};
const finishedContract = (contract: Contract): Contract => {
  const now = new Date();
  const query = { ...(contract.query || {}) };
  query.page = 1;
  query.limit = Math.max(50, Number(query.limit || 50));
  query.fromDate = toGmt7Iso(new Date(now.getTime() - (4 * 60 * 60 * 1000)));
  query.toDate = toGmt7Iso(now);
  query.statuses = ["FINISHED"];
  query.sortMatchTime = "desc";
  query.returnList = ["info"];
  query.isIncludedRelation = "false";
  delete query.companyIds;
  return { ...contract, query };
};
const liveInfoContract = (contract: Contract): Contract => {
  const query = { ...(contract.query || {}) };
  query.page = 1;
  query.limit = Math.max(50, Number(query.limit || 50));
  query.statuses = ["LIVE"];
  query.returnList = ["info"];
  query.isIncludedRelation = "false";
  delete query.companyIds;
  return { ...contract, query };
};
const oddsContract = (contract: Contract, statuses: string[] = ["LIVE", "UPCOMING"]): Contract => ({
  ...contract,
  query: { ...(contract.query || {}), page: 1, statuses },
});
const payloadData = (payload: unknown): Record<string, any> => {
  const root = object(payload); const data = object(root.data ?? root);
  return data;
};
const payloadMatches = (payload: unknown): any[] => {
  const data = payloadData(payload);
  const matches = Array.isArray(data.matches) ? data.matches : Array.isArray(data.items) ? data.items : [];
  return matches.map((match: any) => match?.id ? match : { ...match, id: match?.info?.id }).filter((match: any) => match.id);
};
const logOddsBoardResponse = (contract: Contract, payload: unknown, source: string) => {
  const matches = payloadMatches(payload);
  console.groupCollapsed(`[GWD Odds Compare] odds-board:${source} | matches=${matches.length}`);
  console.log("query", contract.query);
  console.log("raw payload", payload);
  console.table(matches.map((match: any) => ({
    id: String(match?.id || match?.info?.id || ""),
    status: match?.info?.status || match?.info?.statusId || "",
    asia: Boolean(match?.asia || match?.odds?.asia || match?.markets?.asia),
    eu: Boolean(match?.eu || match?.odds?.eu || match?.markets?.eu),
    bs: Boolean(match?.bs || match?.odds?.bs || match?.markets?.bs),
  })));
  const debugMatch = matches.find(
    (match: any) => String(match?.id || match?.info?.id || "") === ODDS_DEBUG_MATCH_ID,
  );
  console.log(`debug match ${ODDS_DEBUG_MATCH_ID}`, debugMatch || "not returned in this page");
  console.groupEnd();
};
const mergePayloadIntoState = (state: BoardState, payload: unknown, finalSnapshot = false, source = "odds-board") => {
  const data = payloadData(payload); const matches = payloadMatches(payload);
  const relation = { ...object(data.relation), teams: object(data.relation).teams || data.teams || [], competitions: object(data.relation).competitions || data.competitions || [] };
  const result = livescoreRuntime.mergeMatches(matches);
  const relationKeys = livescoreRuntime.mergeRelations(relation);
  if (finalSnapshot) state.viewIds = result.ids;
  else { const ids = new Set(state.viewIds); result.ids.forEach((id) => ids.add(id)); state.viewIds = [...ids]; }
  if (result.changedIds.length || result.addedIds.length) livescoreRuntime.emit("matches:update", { changedIds: result.changedIds, addedIds: result.addedIds, source });
  if (relationKeys.length) livescoreRuntime.emit("relations:update", { relationKeys, source });
  return result.ids;
};
const mergeEntityPayloadIntoState = (state: BoardState, payload: unknown, source: string) => {
  const data = payloadData(payload); const matches = payloadMatches(payload);
  const relation = { ...object(data.relation), teams: object(data.relation).teams || data.teams || [], competitions: object(data.relation).competitions || data.competitions || [] };
  const result = livescoreRuntime.mergeMatches(matches);
  const relationKeys = livescoreRuntime.mergeRelations(relation);
  if (result.changedIds.length || result.addedIds.length) livescoreRuntime.emit("matches:update", { changedIds: result.changedIds, addedIds: result.addedIds, source });
  if (relationKeys.length) livescoreRuntime.emit("relations:update", { relationKeys, source });
  return result.ids;
};
const requestPages = async (contract: Contract, signal: AbortSignal, onPage?: (payload: unknown, page: number, snapshotIds: string[]) => void): Promise<string[]> => {
  const limit = Math.max(1, Number(contract.query?.limit || 50));
  const snapshotIds: string[] = []; const matchIds = new Set<string>(); let total = Number.POSITIVE_INFINITY;
  for (let page = 1; page <= 100; page += 1) {
    const payload = object(await requestPage(contract, page, signal)); const data = object(payload.data ?? payload);
    if (page === 1) { const responseTotal = Number(data.total); if (Number.isFinite(responseTotal)) total = responseTotal; }
    const pageMatches = Array.isArray(data.matches) ? data.matches : Array.isArray(data.items) ? data.items : [];
    pageMatches.forEach((match) => { const id = String(match?.id ?? match?.info?.id ?? ""); if (!id || matchIds.has(id)) return; matchIds.add(id); snapshotIds.push(id); });
    onPage?.(payload, page, [...snapshotIds]);
    if (!pageMatches.length || pageMatches.length < limit || snapshotIds.length >= total) break;
  }
  return snapshotIds;
};
const nodeKey = (node: Element): string => {
  if (node instanceof HTMLElement && node.dataset.gwdOddsMatchId) return `match:${node.dataset.gwdOddsMatchId}`;
  if (node instanceof HTMLElement && node.dataset.gwdOddsLeagueId) { const nextMatchId = node.nextElementSibling instanceof HTMLElement ? node.nextElementSibling.dataset.gwdOddsMatchId || "" : ""; return `league:${node.dataset.gwdOddsLeagueId}:${nextMatchId}`; }
  return node.classList.contains("gwd-odds-board__empty") ? "empty" : "";
};
const patchNode = (current: Node, incoming: Node): Node => {
  if (current.nodeType !== incoming.nodeType || (current instanceof Element && incoming instanceof Element && current.tagName !== incoming.tagName)) {
    current.parentNode?.replaceChild(incoming, current);
    return incoming;
  }
  if (current.nodeType === Node.TEXT_NODE) {
    if (current.nodeValue !== incoming.nodeValue) current.nodeValue = incoming.nodeValue;
    return current;
  }
  if (!(current instanceof Element) || !(incoming instanceof Element)) return current;

  Array.from(current.attributes).forEach((attribute) => {
    if (!incoming.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
  });
  Array.from(incoming.attributes).forEach((attribute) => {
    if (current.getAttribute(attribute.name) !== attribute.value) current.setAttribute(attribute.name, attribute.value);
  });

  const incomingChildren = Array.from(incoming.childNodes);
  incomingChildren.forEach((incomingChild, index) => {
    const currentChild = current.childNodes[index];
    if (!currentChild) current.appendChild(incomingChild);
    else patchNode(currentChild, incomingChild);
  });
  while (current.childNodes.length > incomingChildren.length) current.lastChild?.remove();
  return current;
};
const reconcileContent = (content: HTMLElement, html: string, prune = true) => {
  const template = document.createElement("template"); template.innerHTML = html;
  const nextNodes = Array.from(template.content.children);
  if (!prune && nextNodes.length === 1 && nextNodes[0].classList.contains("gwd-odds-board__empty")) return;
  const currentByKey = new Map<string, Element>();
  Array.from(content.children).forEach((node) => { const key = nodeKey(node); if (key) currentByKey.set(key, node); });
  const retained = new Set<Element>(); let reference: Element | null = content.firstElementChild;
  nextNodes.forEach((incoming) => {
    const key = nodeKey(incoming); const current = key ? currentByKey.get(key) : undefined;
    let node = current || incoming;
    if (current && current.outerHTML !== incoming.outerHTML) node = patchNode(current, incoming) as Element;
    if (node !== reference) content.insertBefore(node, reference);
    retained.add(node); reference = node.nextElementSibling;
  });
  if (prune) Array.from(content.children).forEach((node) => { if (!retained.has(node)) node.remove(); });
};
const paint = (root: HTMLElement, state: BoardState, prune = true) => {
  const content = root.querySelector<HTMLElement>("[data-gwd-odds-content]"); const excludedIds = new Set([...state.hiddenIds, ...state.finishedIds]);
  const matches = state.viewIds.map((id) => state.entities.matches[id]).filter(Boolean);
  if (content) reconcileContent(content, renderOddsBoard({ matches, teams: state.entities.teams, competitions: state.entities.competitions }, state.companyId, excludedIds, normalizeOddsFormat(state.contract.oddsFormat || state.contract.query?.oddsFormat)), prune);
  const count = root.querySelector<HTMLElement>("[data-gwd-odds-hidden-count]"); if (count) count.textContent = String(state.hiddenIds.size);
};
const setLoadingMore = (root: HTMLElement, visible: boolean) => {
  const loading = root.querySelector<HTMLElement>("[data-gwd-odds-loading-more]");
  if (loading) loading.hidden = !visible;
};
const updateClocks = (root: HTMLElement, state: BoardState) => root.querySelectorAll<HTMLElement>("[data-gwd-odds-match-id]").forEach((row) => {
  const matchId = String(row.dataset.gwdOddsMatchId || ""); const info = state.entities.matches[matchId]?.info;
  const clock = row.querySelector<HTMLElement>("[data-gwd-odds-clock]"); if (!clock || !info) return;
  const html = formatMatchClockHtml(info); if (clock.innerHTML !== html) clock.innerHTML = html;
});
const pollInterval = (state: BoardState): number => Math.max(3_000, Number(state.contract.pollInterval || 3_000));
const schedulePoll = (root: HTMLElement, state: BoardState) => {
  window.clearTimeout(state.timer);
  state.timer = window.setTimeout(() => void refresh(root, state), pollInterval(state));
};
const refresh = async (root: HTMLElement, state: BoardState, foreground = false, force = false) => {
  if (!force && state.controller) return;
  if (force) state.controller?.abort();
  const controller = new AbortController(); state.controller = controller; const requestId = ++state.requestId;
  const status = root.querySelector<HTMLElement>("[data-gwd-odds-status]"); root.classList.toggle("is-loading", foreground); if (status) status.textContent = foreground ? "Đang tải tỷ lệ kèo..." : "";
  try {
    const liveInfoRequest = requestPages(liveInfoContract(state.contract), controller.signal, (pagePayload) => {
        mergeEntityPayloadIntoState(state, pagePayload, "odds-board-live-info");
      })
      .catch((error) => { if (error instanceof DOMException && error.name === "AbortError") throw error; console.warn("GWD odds board live info request failed", error); return null; });
    const endedRequest = requestPages(finishedContract(state.contract), controller.signal, (pagePayload) => {
        mergeEntityPayloadIntoState(state, pagePayload, "odds-board-finished");
      })
      .then((ids) => new Set(ids))
      .catch((error) => { if (error instanceof DOMException && error.name === "AbortError") throw error; console.warn("GWD odds board finished request failed", error); return null; });
    const snapshotIds: string[] = [];
    if (!state.initialSnapshotLoaded) {
      const liveSnapshotIds: string[] = [];
      for (const status of ["LIVE", "UPCOMING"]) {
        if (status === "UPCOMING") setLoadingMore(root, true);
        const ids = await requestPages(oddsContract(state.contract, [status]), controller.signal, (pagePayload) => {
          if (state.requestId !== requestId) return;
          logOddsBoardResponse(oddsContract(state.contract, [status]), pagePayload, `odds-${status.toLowerCase()}`);
          mergeEntityPayloadIntoState(state, pagePayload, "odds-board-odds");
          const pageIds = payloadMatches(pagePayload).map((match) => String(match.id));
          if (status === "LIVE") {
            pageIds.forEach((id) => { if (!liveSnapshotIds.includes(id)) liveSnapshotIds.push(id); });
            state.viewIds = [...liveSnapshotIds];
          } else {
            pageIds.forEach((id) => { if (!snapshotIds.includes(id)) snapshotIds.push(id); });
            state.viewIds = [...liveSnapshotIds, ...snapshotIds.filter((id) => !liveSnapshotIds.includes(id))];
          }
          paint(root, state, true);
        });
        ids.forEach((id) => { if (!snapshotIds.includes(id)) snapshotIds.push(id); });
      }
      state.initialSnapshotLoaded = true;
    } else {
      snapshotIds.push(...await requestPages(oddsContract(state.contract), controller.signal, (pagePayload) => {
        if (state.requestId !== requestId) return;
        logOddsBoardResponse(oddsContract(state.contract), pagePayload, "odds-poll");
        mergeEntityPayloadIntoState(state, pagePayload, "odds-board-odds");
      }));
    }
    await liveInfoRequest;
    const finishedIds = await endedRequest;
    if (state.requestId !== requestId) return;
    if (finishedIds) state.finishedIds = finishedIds;
    state.viewIds = snapshotIds; state.lastSyncAt = Date.now(); paint(root, state, true); setLoadingMore(root, false); if (status) status.textContent = "";
  }
  catch (error) { if (state.requestId !== requestId || (error instanceof DOMException && error.name === "AbortError")) return; if (status) status.textContent = navigator.onLine ? "Không tải được tỷ lệ kèo." : "Đang ngoại tuyến."; console.warn("GWD odds board request failed", error); }
  finally { if (state.requestId === requestId) { state.controller = null; setLoadingMore(root, false); root.classList.remove("is-loading"); schedulePoll(root, state); } }
};
export const mountOddsBoard = (root: HTMLElement) => {
  if (states.has(root)) return;
  const rawState = root.dataset.gwdState;
  if (!rawState) return;
  let initialState: InitialState;
  try { initialState = JSON.parse(rawState) as InitialState; } catch (error) { console.warn("GWD odds board initial state is invalid", error); return; }
  delete root.dataset.gwdState;
  const contract = initialState.request || {};
  const state: BoardState = { contract, companyId: String(contract.activeCompanyId || "14"), entities: livescoreRuntime.entities, viewIds: [], hiddenIds: readHidden(), finishedIds: new Set(), initialSnapshotLoaded: false, requestId: 0, timer: 0, clockTimer: 0, controller: null, lastSyncAt: Date.now(), unsubscribe: null };
  states.set(root, state); mergePayloadIntoState(state, initialState.data || {}, true, "odds-board-initial");
  const unsubscribeMatches = livescoreRuntime.subscribe("matches:update", ({ changedIds = [], addedIds = [] }) => {
    const visibleIds = new Set(state.viewIds); if ([...changedIds, ...addedIds].some((id) => visibleIds.has(id))) paint(root, state, false);
  });
  const unsubscribeRelations = livescoreRuntime.subscribe("relations:update", () => paint(root, state, false));
  state.unsubscribe = () => { unsubscribeMatches(); unsubscribeRelations(); };
  paint(root, state); logOddsPayload(state, "initial");
  root.addEventListener("click", (event) => { const target = event.target instanceof Element ? event.target : null; const company = target?.closest<HTMLElement>("[data-gwd-odds-company-id]"); if (company?.dataset.gwdOddsCompanyId) { state.companyId = company.dataset.gwdOddsCompanyId; root.querySelectorAll<HTMLElement>("[data-gwd-odds-company-id]").forEach((button) => button.classList.toggle("is-active", button === company)); logOddsPayload(state, "bookmaker-click"); paint(root, state); return; } if (target?.closest("[data-gwd-odds-show-all]")) { state.hiddenIds.clear(); writeHidden(state.hiddenIds); paint(root, state); return; } const hide = target?.closest<HTMLElement>("[data-gwd-odds-hide]"); const match = hide?.closest<HTMLElement>("[data-gwd-odds-match-id]"); if (match?.dataset.gwdOddsMatchId) { state.hiddenIds.add(match.dataset.gwdOddsMatchId); writeHidden(state.hiddenIds); paint(root, state); } });
  // A browser-tab restore commonly emits focus and pageshow together. Do not
  // abort/restart the first request for every one of them.
  const wake = () => {
    if (state.controller) return;
    window.clearTimeout(state.timer);
    void refresh(root, state);
  };
  window.addEventListener("focus", wake);
  window.addEventListener("online", wake);
  window.addEventListener("offline", () => { const status = root.querySelector<HTMLElement>("[data-gwd-odds-status]"); if (status) status.textContent = "Đang ngoại tuyến."; });
  window.addEventListener("pageshow", wake);
  void refresh(root, state);
  state.clockTimer = window.setInterval(() => updateClocks(root, state), 15_000);
};
export const oddsBoardDefaultLayout: LayoutRuntime = { feature: "odds-board", layout: "default", rootClass: "gwd-livescore-layout--default", mount: mountOddsBoard };
